// src/components/dashboard/PaymentsSection.tsx
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { apiService, PaymentMethod } from '../../services/api'
import toast from 'react-hot-toast'
import { 
  Wallet, 
  Plus, 
  Trash2, 
  Edit2, 
  Building2, 
  CreditCard,
  User,
  Hash,
  CheckCircle,
  X,
  Loader2,
  AlertCircle,
  Shield,
  Star,
  DollarSign,
  Landmark,
  ChevronDown
} from 'lucide-react'

// ============== Ödeme Sağlayıcıları ==============
interface ProviderField {
  key: 'accountIdentifier' | 'iban'
  label: string
  placeholder: string
  type: string
  hint?: string
}
interface ProviderConfig {
  id: string
  label: string
  logo?: string     // public/ altındaki yerel logo (TR IBAN'da yok → ikon)
  color: string     // logo yüklenemezse metin/rozet rengi
  currency: string  // USD | TRY
  scale?: number    // logodaki boşluğa göre görsel boyut ince ayarı (Cenoa baz = 1)
  field: ProviderField
}

const PROVIDERS: ProviderConfig[] = [
  { id: 'Cenoa', label: 'Cenoa', logo: '/logos/cenoa.png', color: '#1f44d6', currency: 'USD',
    field: { key: 'accountIdentifier', label: 'Telefon Numarası', placeholder: '+90 5XX XXX XX XX', type: 'tel', hint: 'Cenoa para gönderimi için telefon numarası yeterli.' } },
  { id: 'Wise', label: 'Wise', logo: '/logos/wise.png', color: '#163300', currency: 'USD',
    field: { key: 'accountIdentifier', label: 'Wise Etiketi (Wisetag)', placeholder: '@kullaniciadi', type: 'text', hint: 'Wise profilindeki @etiketin.' } },
  { id: 'WorldFirst', label: 'WorldFirst', logo: '/logos/worldfirst.svg', color: '#ff4f00', currency: 'USD',
    field: { key: 'accountIdentifier', label: 'Account ID', placeholder: '5364************', type: 'text', hint: 'WorldFirst hesabındaki Account ID.' } },
  { id: 'Payoneer', label: 'Payoneer', logo: '/logos/payoneer.png', color: '#ff4800', currency: 'USD',
    field: { key: 'accountIdentifier', label: 'Payoneer E-posta', placeholder: 'ornek@mail.com', type: 'email', hint: 'Payoneer hesabının e-posta adresi.' } },
  { id: 'TR IBAN', label: 'TR IBAN', color: '#0ea5e9', currency: 'TRY',
    field: { key: 'iban', label: 'IBAN', placeholder: 'TR00 0000 0000 0000 0000 0000 00', type: 'text', hint: 'TR ile başlayan 26 karakterli IBAN.' } },
]

const getProvider = (id: string): ProviderConfig =>
  PROVIDERS.find(p => p.id === id) || PROVIDERS[0]

// Logo etiketi: wordmark görseli (ismi içinde) — beyaz arka plan mix-blend ile kaybolur.
// TR IBAN'da banka ikonu + metin, logo yüklenemezse marka renginde metin.
function ProviderLogo({ provider, height = 22 }: { provider: ProviderConfig; height?: number }) {
  const [err, setErr] = useState(false)
  // Sağlayıcı değişince hata durumunu sıfırla (kapalı kutuda eski hata takılı kalmasın)
  useEffect(() => { setErr(false) }, [provider.id])
  if (provider.id === 'TR IBAN') {
    return (
      <span className="flex items-center gap-2">
        <Landmark style={{ width: height, height }} className="text-sky-600" />
        <span className="font-semibold text-gray-800">TR IBAN</span>
      </span>
    )
  }
  if (!provider.logo || err) {
    return <span className="font-bold" style={{ color: provider.color }}>{provider.label}</span>
  }
  return (
    <img
      src={provider.logo}
      alt={provider.label}
      onError={() => setErr(true)}
      style={{ maxHeight: height * (provider.scale ?? 1), maxWidth: 150 * (provider.scale ?? 1), width: 'auto', height: 'auto' }}
      className="object-contain mix-blend-multiply"
    />
  )
}

interface PaymentsSectionProps {}

export default function PaymentsSection({}: PaymentsSectionProps) {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [providerOpen, setProviderOpen] = useState(false)

  const [formData, setFormData] = useState({
    accountOwnerType: 'Bireysel',
    currency: 'USD',
    paymentProvider: 'Cenoa',
    payoutMethod: 'PROVIDER',
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    bankAccountType: 'Checking',
    iban: '',
    accountIdentifier: '',
    setAsPrimary: false
  })

  const [formErrors, setFormErrors] = useState({
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    bankAccountType: '',
    iban: '',
    accountIdentifier: ''
  })

  useEffect(() => {
    fetchPaymentMethods()
  }, [])

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true)
      const methods = await apiService.getPaymentMethods()
      setPaymentMethods(methods)
    } catch (error) {
      console.error('Payment methods fetch error:', error)
      toast.error('Ödeme yöntemleri yüklenemedi')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      accountOwnerType: 'Bireysel',
      currency: 'USD',
      paymentProvider: 'Cenoa',
      payoutMethod: 'PROVIDER',
      accountHolderName: '',
      routingNumber: '',
      accountNumber: '',
      bankAccountType: 'Checking',
      iban: '',
      accountIdentifier: '',
      setAsPrimary: false
    })
    setFormErrors({
      accountHolderName: '',
      routingNumber: '',
      accountNumber: '',
      bankAccountType: '',
      iban: '',
      accountIdentifier: ''
    })
    setProviderOpen(false)
  }

  const openAddModal = () => {
    resetForm()
    setShowAddModal(true)
  }

  const openEditModal = (method: PaymentMethod) => {
    setSelectedMethod(method)
    setFormData({
      accountOwnerType: method.accountOwnerType || 'Bireysel',
      currency: method.currency || 'USD',
      paymentProvider: method.paymentProvider || 'Cenoa',
      payoutMethod: method.payoutMethod || 'PROVIDER',
      accountHolderName: method.accountHolderName || '',
      routingNumber: method.routingNumber || '',
      accountNumber: method.accountNumber || '',
      bankAccountType: method.bankAccountType || 'Checking',
      iban: method.iban || '',
      accountIdentifier: method.accountIdentifier || '',
      setAsPrimary: method.isPrimary || false
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (method: PaymentMethod) => {
    setSelectedMethod(method)
    setShowDeleteModal(true)
  }

  const closeModals = () => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowDeleteModal(false)
    setSelectedMethod(null)
    resetForm()
  }

  const validateForm = () => {
    const errors = {
      accountHolderName: '',
      routingNumber: '',
      accountNumber: '',
      bankAccountType: '',
      iban: '',
      accountIdentifier: ''
    }
    let isValid = true

    // Ad Soyad
    if (!formData.accountHolderName.trim()) {
      errors.accountHolderName = 'Ad Soyad gereklidir'
      isValid = false
    } else if (formData.accountHolderName.trim().length < 3) {
      errors.accountHolderName = 'Ad Soyad en az 3 karakter olmalıdır'
      isValid = false
    }

    const provider = getProvider(formData.paymentProvider)

    if (provider.id === 'TR IBAN') {
      const ibanClean = formData.iban.replace(/\s/g, '').toUpperCase()
      if (!ibanClean) {
        errors.iban = 'IBAN gereklidir'
        isValid = false
      } else if (!/^TR\d{24}$/.test(ibanClean)) {
        errors.iban = 'IBAN TR + 24 rakam formatında olmalıdır'
        isValid = false
      }
    } else {
      const val = formData.accountIdentifier.trim()
      if (!val) {
        errors.accountIdentifier = `${provider.field.label} gereklidir`
        isValid = false
      } else if (provider.id === 'Payoneer' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) {
        errors.accountIdentifier = 'Geçerli bir e-posta adresi girin'
        isValid = false
      } else if (provider.id === 'Cenoa' && !/^\+?\d{7,15}$/.test(val.replace(/[\s\-().]/g, ''))) {
        errors.accountIdentifier = 'Geçerli bir telefon numarası girin'
        isValid = false
      } else if (val.length < 2) {
        errors.accountIdentifier = 'Geçersiz değer'
        isValid = false
      }
    }

    setFormErrors(errors)
    return isValid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Lütfen formu doğru şekilde doldurun')
      return
    }

    setSubmitting(true)
    try {
      const provider = getProvider(formData.paymentProvider)
      const isIban = provider.id === 'TR IBAN'
      const payload = {
        accountOwnerType: formData.accountOwnerType,
        currency: provider.currency,
        paymentProvider: provider.id,
        payoutMethod: isIban ? 'IBAN' : 'PROVIDER',
        accountHolderName: formData.accountHolderName.trim(),
        routingNumber: null,
        accountNumber: null,
        bankAccountType: null,
        iban: isIban ? formData.iban.replace(/\s/g, '').toUpperCase() : null,
        accountIdentifier: isIban ? null : formData.accountIdentifier.trim(),
        setAsPrimary: formData.setAsPrimary
      }

      if (showEditModal && selectedMethod) {
        await apiService.updatePaymentMethod(selectedMethod.id, payload)
        toast.success('Ödeme yöntemi güncellendi')
      } else {
        await apiService.addPaymentMethod(payload)
        toast.success('Ödeme yöntemi eklendi')
      }

      await fetchPaymentMethods()
      closeModals()
    } catch (error: any) {
      console.error('Payment method submit error:', error)
      const errorMessage = error.message || 'İşlem başarısız oldu'
      toast.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedMethod) return

    setSubmitting(true)
    try {
      await apiService.deletePaymentMethod(selectedMethod.id)
      toast.success('Ödeme yöntemi silindi')
      await fetchPaymentMethods()
      closeModals()
    } catch (error: any) {
      console.error('Payment method delete error:', error)
      const errorMessage = error.message || 'Silme işlemi başarısız oldu'
      toast.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSetPrimary = async (methodId: string) => {
    try {
      await apiService.setPrimaryPaymentMethod(methodId)
      toast.success('Varsayılan ödeme yöntemi güncellendi')
      await fetchPaymentMethods()
    } catch (error: any) {
      console.error('Set primary error:', error)
      const errorMessage = error.message || 'İşlem başarısız oldu'
      toast.error(errorMessage)
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }
      // Sağlayıcı değişince para birimi ve yöntemi otomatik ayarla
      if (field === 'paymentProvider') {
        const p = getProvider(value as string)
        updated.currency = p.currency
        updated.payoutMethod = p.id === 'TR IBAN' ? 'IBAN' : 'PROVIDER'
      }
      return updated
    })

    // Clear error when user types
    if (field in formErrors) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const formatIban = (iban: string) => {
    if (!iban) return ''
    return iban.replace(/(.{4})/g, '$1 ').trim()
  }

  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber || accountNumber.length < 4) return accountNumber
    const visible = accountNumber.slice(-4)
    return '**** **** ' + visible
  }

  const maskRoutingNumber = (routingNumber: string) => {
    if (!routingNumber || routingNumber.length < 4) return routingNumber
    const visible = routingNumber.slice(-4)
    return '*****' + visible
  }

  const getCurrencySymbol = (currency: string) => {
    return currency === 'USD' ? '$' : '₺'
  }

  const getCurrencyFlag = (currency: string) => {
    return currency === 'USD' ? '🇺🇸' : '🇹🇷'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
          <p className="text-gray-600 font-medium">Ödeme yöntemleri yükleniyor...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Ödeme Yöntemleri</h2>
            <p className="text-gray-600 mt-1">Banka hesap bilgilerinizi ekleyin ve yönetin</p>
          </div>
          <Button
            onClick={openAddModal}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Hesap Ekle
          </Button>
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 mb-1">Güvenli Ödeme</p>
            <p className="text-sm text-blue-700">
              Ödeme bilgileriniz güvenle saklanır. Cenoa, Wise, WorldFirst, Payoneer veya TR IBAN ile ödeme alabilirsiniz.
              Bonus ödemeleriniz varsayılan hesabınıza yapılacaktır.
            </p>
          </div>
        </div>

        {/* Payment Methods List */}
        {paymentMethods.length === 0 ? (
          <Card className="glass border-orange-200 shadow-lg">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-10 h-10 text-orange-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Henüz ödeme yöntemi eklemediniz
              </h3>
              <p className="text-gray-600 mb-6">
                Bonus ödemelerinizi alabilmek için bir banka hesabı ekleyin
              </p>
              <Button
                onClick={openAddModal}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                İlk Hesabı Ekle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {paymentMethods.map((method, index) => (
              <motion.div
                key={method.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`glass shadow-lg hover:shadow-xl transition-all ${
                  method.isPrimary ? 'border-2 border-orange-400 ring-2 ring-orange-100' : 'border-orange-200'
                }`}>
                  <CardContent className="p-6">
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        {(() => {
                          const known = PROVIDERS.find(p => p.id === method.paymentProvider)
                          return known
                            ? <ProviderLogo provider={known} height={20} />
                            : <span className="font-semibold text-gray-800">{method.payoutMethod === 'ACH' ? 'ACH Hesap' : (method.paymentProvider || 'Hesap')}</span>
                        })()}
                        <p className="text-sm text-gray-500 mt-1">{method.accountOwnerType}</p>
                      </div>
                      
                      {method.isPrimary && (
                        <div className="flex items-center space-x-1 bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                          <Star className="w-3 h-3 fill-current" />
                          <span>Varsayılan</span>
                        </div>
                      )}
                    </div>

                    {/* Account Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Hesap Sahibi:</span>
                        <span className="font-medium text-gray-800">{method.accountHolderName}</span>
                      </div>

                      {method.accountIdentifier ? (
                        <div className="flex items-center space-x-2 text-sm">
                          <Hash className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">
                            {PROVIDERS.find(p => p.id === method.paymentProvider)?.field.label || 'Hesap'}:
                          </span>
                          <span className="font-mono text-gray-800 break-all">{method.accountIdentifier}</span>
                        </div>
                      ) : method.payoutMethod === 'ACH' ? (
                        <>
                          <div className="flex items-center space-x-2 text-sm">
                            <Hash className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Routing:</span>
                            <span className="font-mono text-gray-800">
                              {maskRoutingNumber(method.routingNumber || '')}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <CreditCard className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Hesap No:</span>
                            <span className="font-mono text-gray-800">
                              {maskAccountNumber(method.accountNumber || '')}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Wallet className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Tür:</span>
                            <span className="font-medium text-gray-800">{method.bankAccountType}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center space-x-2 text-sm">
                          <Hash className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">IBAN:</span>
                          <span className="font-mono text-gray-800 text-xs">
                            {formatIban(method.iban || '')}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center space-x-2 text-sm">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Para Birimi:</span>
                        <span className="font-medium text-gray-800">
                          {method.currency} {getCurrencySymbol(method.currency)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center space-x-2">
                        {!method.isPrimary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetPrimary(method.id)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Varsayılan Yap
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditModal(method)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDeleteModal(method)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeModals}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-800">
                    {showEditModal ? 'Ödeme Yöntemini Düzenle' : 'Yeni Ödeme Yöntemi Ekle'}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closeModals}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Banka / Ödeme Sağlayıcı */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Banka / Ödeme Sağlayıcı
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setProviderOpen(o => !o)}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 bg-white"
                    >
                      <span className="flex items-center">
                        <ProviderLogo provider={getProvider(formData.paymentProvider)} height={18} />
                      </span>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${providerOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {providerOpen && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        {PROVIDERS.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { handleInputChange('paymentProvider', p.id); setProviderOpen(false) }}
                            className={`w-full flex items-center gap-3 p-3 text-left hover:bg-orange-50 ${formData.paymentProvider === p.id ? 'bg-orange-50' : ''}`}
                          >
                            <ProviderLogo provider={p} height={18} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Owner Type */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Hesap Türü
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleInputChange('accountOwnerType', 'Bireysel')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.accountOwnerType === 'Bireysel'
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <User className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Bireysel</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange('accountOwnerType', 'Kurumsal')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.accountOwnerType === 'Kurumsal'
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Building2 className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Kurumsal</span>
                    </button>
                  </div>
                </div>

                {/* Account Holder Name */}
                <div>
                  <label htmlFor="accountHolderName" className="text-sm font-medium text-gray-700 mb-2 block">
                    Hesap Sahibi Adı *
                  </label>
                  <Input
                    id="accountHolderName"
                    value={formData.accountHolderName}
                    onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                    placeholder="Adınız Soyadınız"
                    className={formErrors.accountHolderName ? 'border-red-500' : ''}
                  />
                  {formErrors.accountHolderName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {formErrors.accountHolderName}
                    </p>
                  )}
                </div>

                {/* Sağlayıcıya özel alan (dinamik) */}
                {(() => {
                  const p = getProvider(formData.paymentProvider)
                  const isIban = p.field.key === 'iban'
                  const value = isIban ? formData.iban : formData.accountIdentifier
                  const err = isIban ? formErrors.iban : formErrors.accountIdentifier
                  return (
                    <div>
                      <label htmlFor="providerField" className="text-sm font-medium text-gray-700 mb-2 block">
                        {p.field.label} *
                      </label>
                      <Input
                        id="providerField"
                        type={p.field.type}
                        value={value}
                        onChange={(e) => handleInputChange(p.field.key, isIban ? e.target.value.toUpperCase() : e.target.value)}
                        placeholder={p.field.placeholder}
                        className={err ? 'border-red-500' : ''}
                      />
                      {err && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {err}
                        </p>
                      )}
                      {p.field.hint && <p className="mt-1 text-xs text-gray-500">{p.field.hint}</p>}
                    </div>
                  )
                })()}

                {/* Set as Primary */}
                <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="setAsPrimary"
                    checked={formData.setAsPrimary}
                    onChange={(e) => handleInputChange('setAsPrimary', e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="setAsPrimary" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Bu hesabı varsayılan ödeme yöntemi olarak ayarla
                  </label>
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeModals}
                    disabled={submitting}
                  >
                    İptal
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {showEditModal ? 'Güncelleniyor...' : 'Ekleniyor...'}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {showEditModal ? 'Güncelle' : 'Ekle'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedMethod && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeModals}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Ödeme Yöntemini Sil</h3>
                  <p className="text-sm text-gray-600">Bu işlem geri alınamaz</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                <span className="font-medium">{selectedMethod.accountHolderName}</span> adına kayıtlı{' '}
                {selectedMethod.payoutMethod === 'ACH' ? 'ACH hesabını' : 'IBAN hesabını'} silmek istediğinize emin misiniz?
              </p>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={closeModals}
                  disabled={submitting}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Sil
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}